import { format } from 'date-fns'
import { TOOLTIP } from '../../../../constants/tooltips'
import { FormFieldsEdit } from '../../../../types/form-types'
import { useWellState } from '../../../context/well-context'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import useUser from '../../../hooks/use-user'
import { DISPLAY_DATE_FORMAT, INTERNAL_DATE_FORMAT } from '../../../services/constants'
import { isDriller } from '../../../services/user-utils'
import { parseDate } from '../../aoraki/generic-search-common/src'
import LookupField from '../../wells/lookup-field'
import ReadOnlyLookupField from '../../wells/readonly-lookup-field'
import { WellDetailsComponentBase } from './well-details-types'
import { preventChangeOnScroll } from '../../../services/form-utils'
import { CheckableField, CheckableFields, DateField, FormField, Heading, InputWidth, TextInput } from '@msaf/core-react'

interface WellDetailsDrillingEditProps extends WellDetailsComponentBase {
  isExistingOrDecommissioned?: boolean
  isWellGrantedAsLegacy?: boolean
  handleCompanyValidation?: (option: any) => void
}

interface InputDrillingMeasurementsProps {
  isLoading: boolean
  validationErrors: ValidationErrors
  value: string
  setValue: SetValue<FormFieldsEdit>
  label: string
  htmlFor: string
  fieldName: keyof FormFieldsEdit
  isRequired?: boolean
  toolTipMessage?: string
}

function InputDrillingMeasurements({
  isLoading,
  validationErrors,
  setValue,
  value,
  fieldName,
  htmlFor,
  label,
  toolTipMessage,
  isRequired = false,
}: InputDrillingMeasurementsProps) {
  const validationMessages = validationErrors?.[fieldName]

  return (
    <FormField
      isSkeleton={isLoading}
      validationMessages={validationMessages}
      htmlFor={htmlFor}
      label={label}
      isRequired={isRequired}
      toolTipMessage={toolTipMessage}
    >
      <TextInput
        isSkeleton={isLoading}
        id={htmlFor}
        value={value}
        setValue={(value) => setValue(fieldName, value)}
        isInvalid={!!validationMessages}
        onWheel={preventChangeOnScroll}
      />
    </FormField>
  )
}

export default function WellDetailsDrillingEdit({
  isLoading,
  isSetupRecord,
  setValue,
  validationErrors,
  fields,
  isExistingOrDecommissioned,
  isWellGrantedAsLegacy,
  handleCompanyValidation,
}: WellDetailsDrillingEditProps) {
  const { user } = useUser()
  const { wellState } = useWellState()

  const drillerValidationHandler = (option: any) => {
    if (option && handleCompanyValidation) {
      handleCompanyValidation(option)
    }
  }

  const isSetup = wellState.isDraft && isSetupRecord
  const showProposedFieldsOnSetup = isSetup && !isExistingOrDecommissioned
  const showActualFieldsOnSetup = isSetup && isExistingOrDecommissioned
  const drillingFieldsAreRequired = !isWellGrantedAsLegacy

  return (
    <>
      <Heading isSkeleton={isLoading} level={3}>
        Drilling
      </Heading>

      {/*
        If the current page is 'Setup' the depth and diameter fields are displayed here. Otherwise they are displayed further down in the form.
        Conditionally show either the proposed or actual fields based on whether the well is existing or decommissioned.
        We only want the user to enter either proposed or actual values on setup, relevant to the well state at time of initial creation in the system.
      */}
      {(showProposedFieldsOnSetup || !isSetup) && (
        <>
          <InputDrillingMeasurements
            toolTipMessage={TOOLTIP.proposedMaxDepthDrilled}
            isLoading={isLoading}
            label='Proposed depth (m)'
            htmlFor='proposedDepth'
            fieldName='proposedDepth'
            validationErrors={validationErrors}
            setValue={setValue}
            value={`${fields.proposedDepth ?? ''}`}
          />
          <InputDrillingMeasurements
            toolTipMessage={TOOLTIP.proposedMaxDiameter}
            isLoading={isLoading}
            label='Proposed diameter (mm)'
            htmlFor='proposedDiameter'
            fieldName='proposedDiameter'
            validationErrors={validationErrors}
            setValue={setValue}
            value={`${fields.proposedDiameter ?? ''}`}
          />
        </>
      )}

      {showActualFieldsOnSetup && (
        <>
          <InputDrillingMeasurements
            toolTipMessage={TOOLTIP.maxDepthDrilled}
            isLoading={isLoading}
            label='Drilled depth (m)'
            htmlFor='actualDepthDrilled'
            fieldName='actualDepthDrilled'
            validationErrors={validationErrors}
            setValue={setValue}
            value={`${fields.actualDepthDrilled ?? ''}`}
          />
          <InputDrillingMeasurements
            toolTipMessage={TOOLTIP.actualMaxDiameter}
            isLoading={isLoading}
            label='Maximum diameter (mm)'
            htmlFor='actualMaxDiameter'
            fieldName='actualMaxDiameter'
            validationErrors={validationErrors}
            setValue={setValue}
            value={`${fields.actualMaxDiameter ?? ''}`}
          />
        </>
      )}

      {/* Drilling company dropdown is on setup page and details page */}
      {user && (
        <FormField
          isSkeleton={isLoading}
          label='Drilling company'
          labelId='drillingCompany'
          // If user is driller - company automatically selected otherwise required
          // Don't show validation errors for readonly field
          validationMessages={user && isDriller(user) ? [] : validationErrors?.companyId}
        >
          {isDriller(user) ? (
            <ReadOnlyLookupField
              isSkeleton={isLoading}
              labelledBy='drillingCompany'
              fieldId='drillingCompany'
              value={`${user?.companyId ?? ''}`}
            />
          ) : (
            <LookupField
              isSkeleton={isLoading}
              labelledBy='drillingCompany'
              fieldId='drillingCompany'
              value={`${fields.drillingCompany ?? ''}`}
              setValue={({ value }) => setValue('drillingCompany', value)}
              handleChangeCallback={(option) => drillerValidationHandler(option)}
            />
          )}
        </FormField>
      )}

      {/*
        These fields are shown only on the details page.
        Since the details page is available throughout the lifecycle of the well,
        the proposed and actual values can be updated as information becomes available.
      */}
      {!isSetupRecord && (
        <>
          <FormField
            isSkeleton={isLoading}
            label='Drillers name'
            htmlFor='drillersName'
            validationMessages={validationErrors?.drillersName}
          >
            <TextInput
              isSkeleton={isLoading}
              id='drillersName'
              value={`${fields.drillersName ?? ''}`}
              setValue={(value) => setValue('drillersName', value)}
            />
          </FormField>
          <FormField
            isSkeleton={isLoading}
            label='Drilling method'
            labelId='drillingMethod'
            validationMessages={validationErrors?.drillingMethod}
          >
            <LookupField
              isSkeleton={isLoading}
              labelledBy='drillingMethod'
              fieldId='drillingMethod'
              value={`${fields.drillingMethod ?? ''}`}
              setValue={({ value }) => setValue('drillingMethod', value)}
            />
          </FormField>
          <FormField
            isSkeleton={isLoading}
            label='Development method'
            labelId='developmentMethod'
            validationMessages={validationErrors?.developmentMethod}
          >
            <LookupField
              isSkeleton={isLoading}
              labelledBy='developmentMethod'
              fieldId='developmentMethod'
              value={`${fields.developmentMethod ?? ''}`}
              setValue={({ value }) => setValue('developmentMethod', value)}
            />
          </FormField>

          <FormField
            isSkeleton={isLoading}
            label='Drilling date'
            htmlFor='drillingDate'
            isRequired={drillingFieldsAreRequired}
            validationMessages={validationErrors?.drillingDate}
          >
            <DateField
              isSkeleton={isLoading}
              isDisabled={isLoading}
              fieldSize={InputWidth.large}
              dateFormat={DISPLAY_DATE_FORMAT}
              selectedDate={parseDate(fields.drillingDate)}
              setSelectedDate={(date: Date) => setValue('drillingDate', date ? format(date, INTERNAL_DATE_FORMAT) : '')}
              isInvalid={!!validationErrors?.drillingDate}
            />
          </FormField>

          <InputDrillingMeasurements
            toolTipMessage={TOOLTIP.maxDepthDrilled}
            isLoading={isLoading}
            isRequired={drillingFieldsAreRequired}
            label='Drilled depth (m)'
            htmlFor='actualDepthDrilled'
            fieldName='actualDepthDrilled'
            validationErrors={validationErrors}
            setValue={setValue}
            value={`${fields.actualDepthDrilled ?? ''}`}
          />
          <InputDrillingMeasurements
            toolTipMessage={TOOLTIP.actualMaxDiameter}
            isLoading={isLoading}
            label='Maximum diameter (mm)'
            htmlFor='actualMaxDiameter'
            fieldName='actualMaxDiameter'
            validationErrors={validationErrors}
            setValue={setValue}
            value={`${fields.actualMaxDiameter ?? ''}`}
          />

          <FormField
            validationMessages={validationErrors?.waterLevelAboveMeasuringPoint}
            isSkeleton={isLoading}
            label='Water level'
            labelId='waterLevelAboveMeasuringPointLabelId'
            isRequired={!!fields.staticWaterLevel}
          >
            <CheckableFields ariaLabelledBy='waterLevelAboveMeasuringPointLabelId' isHorizontal>
              <CheckableField
                isSkeleton={isLoading}
                name='waterLevelAboveMeasuringPoint'
                id='waterLevelAboveMeasuringPointTrue'
                type='radio'
                label='Above measuring point'
                checked={fields.waterLevelAboveMeasuringPoint ?? false}
                onChange={() => setValue('waterLevelAboveMeasuringPoint', true)}
                describedBy='waterLevelAboveMeasuringPointLabelId'
              />
              <CheckableField
                isSkeleton={isLoading}
                name='waterLevelAboveMeasuringPoint'
                id='waterLevelBelowMeasuringPointFalse'
                type='radio'
                label='Below measuring point'
                checked={fields.waterLevelAboveMeasuringPoint === false}
                onChange={() => setValue('waterLevelAboveMeasuringPoint', false)}
                describedBy='waterLevelAboveMeasuringPointLabelId'
              />
            </CheckableFields>
          </FormField>
          <FormField isSkeleton={isLoading} labelId='staticWaterLevel' label='Static water level (m)'>
            <TextInput
              fieldSize={InputWidth.large}
              isSkeleton={isLoading}
              id='staticWaterLevel'
              type='number'
              onWheel={preventChangeOnScroll}
              value={`${fields.staticWaterLevel ?? ''}`}
              setValue={(value) => setValue('staticWaterLevel', value)}
              isInvalid={!!validationErrors?.staticWaterLevel}
            />
          </FormField>
          <FormField
            isSkeleton={isLoading}
            label='Static water level date'
            htmlFor='staticWaterLevelMeasuredDate'
            isRequired={false}
            validationMessages={validationErrors?.staticWaterLevelMeasuredDate}
          >
            <DateField
              isSkeleton={isLoading}
              isDisabled={isLoading}
              fieldSize={InputWidth.large}
              dateFormat={DISPLAY_DATE_FORMAT}
              selectedDate={parseDate(fields.staticWaterLevelMeasuredDate)}
              setSelectedDate={(date: Date) =>
                setValue('staticWaterLevelMeasuredDate', date ? format(date, INTERNAL_DATE_FORMAT) : '')
              }
              isInvalid={!!validationErrors?.staticWaterLevelMeasuredDate}
            />
          </FormField>
        </>
      )}
    </>
  )
}
